/**
 * LeadSuccess — /#/leads/:id/success
 *
 * Step 4 of the Lead-to-AE demo ribbon.
 * Celebrates the completed conversion with a checkmark animation,
 * then offers "View prospect" (account detail) + "Add another lead".
 *
 * No confetti, no emojis. Navy/red brand. Subtle pulse animation.
 * Auto-advance to "View prospect" after 5 seconds (cancelable).
 */

import { useEffect, useRef, useState } from "react";
import { useLocation, useParams } from "wouter";
import { useToast } from "@/hooks/use-toast";
import { RibbonProgress } from "./RibbonProgress";

export default function LeadSuccess() {
  const params = useParams<{ id: string }>();
  const [location, navigate] = useLocation();
  const { toast } = useToast();

  // Parse query string — wouter hash routing places params in the hash fragment,
  // e.g. /#/leads/4/success?accountId=83&aeName=Jordan.
  // Extract from both window.location.search (outer) and the hash fragment.
  const searchParams = (() => {
    // Try hash fragment first: "#/leads/4/success?accountId=83&aeName=..."
    const hash = window.location.hash; // "#/leads/4/success?accountId=83&aeName=..."
    const qIdx = hash.indexOf("?");
    if (qIdx !== -1) return new URLSearchParams(hash.slice(qIdx + 1));
    // Fallback: outer query string
    return new URLSearchParams(window.location.search);
  })();
  const accountId = searchParams.get("accountId") ?? "";
  const aeName = decodeURIComponent(searchParams.get("aeName") ?? "your AE");
  // P0-5 — status=disqualified|nurture|invalid is set when the BDR closed the loop
  const closedStatus = searchParams.get("status") ?? "";
  const isClosedLoop = ["disqualified", "nurture", "invalid"].includes(closedStatus);

  const [countdown, setCountdown] = useState(5);
  const [autoNavigate, setAutoNavigate] = useState(true);
  const intervalRef = useRef<ReturnType<typeof setInterval>>();
  const [checkVisible, setCheckVisible] = useState(false);

  // Show checkmark on mount with a short delay
  useEffect(() => {
    const t = setTimeout(() => setCheckVisible(true), 80);
    return () => clearTimeout(t);
  }, []);

  // Countdown to auto-navigate to account detail (skip for closed-loop
  // BDR dispositions — there is no AE account to view).
  useEffect(() => {
    if (isClosedLoop) { setAutoNavigate(false); setCountdown(0); return; }
    if (!autoNavigate || !accountId) return;

    intervalRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(intervalRef.current);
          navigate(`/account/${accountId}`);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(intervalRef.current);
  }, [autoNavigate, accountId]);

  function cancelAutoNavigate() {
    clearInterval(intervalRef.current);
    setAutoNavigate(false);
    setCountdown(0);
  }

  function handleViewProspect() {
    cancelAutoNavigate();
    if (accountId) {
      navigate(`/account/${accountId}`);
    } else {
      navigate("/dashboards/ae");
    }
  }

  function handleAddAnother() {
    cancelAutoNavigate();
    navigate("/leads/new");
  }

  return (
    <div className="min-h-screen bg-[#FAFAF7]">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white border-b border-neutral-200">
        <div className="max-w-2xl mx-auto px-5 py-3.5 flex items-center justify-between gap-4">
          <div>
            <div className="text-[9px] uppercase tracking-[0.2em] text-neutral-500 leading-none mb-0.5">Mi6 Attilius</div>
            <div className="text-sm font-bold text-[#0B0B0F] leading-none">Prospect created</div>
          </div>
          <div className="text-[10px] text-neutral-500">Step 5 of 5</div>
        </div>
        <RibbonProgress step={5} />
      </header>

      <main className="max-w-2xl mx-auto px-5 py-12">
        {/* Checkmark hero */}
        <div className="flex flex-col items-center text-center mb-10">
          {/* Animated checkmark circle */}
          <div
            className={`w-20 h-20 rounded-full flex items-center justify-center mb-6 transition-all duration-700 ${
              checkVisible
                ? "opacity-100 scale-100"
                : "opacity-0 scale-50"
            }`}
            style={{
              background: "linear-gradient(135deg, #0B0B0F, #1F2937)",
              boxShadow: checkVisible
                ? "0 0 0 12px #0B0B0F15, 0 8px 32px #0B0B0F20"
                : "none",
            }}
          >
            <svg
              width="36"
              height="36"
              viewBox="0 0 36 36"
              fill="none"
              className={`transition-all duration-500 delay-300 ${
                checkVisible ? "opacity-100" : "opacity-0"
              }`}
              aria-hidden
            >
              <path
                d="M8 18l7 7 13-14"
                stroke="white"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{
                  strokeDasharray: 40,
                  strokeDashoffset: checkVisible ? 0 : 40,
                  transition: "stroke-dashoffset 0.5s ease-out 0.4s",
                }}
              />
            </svg>
          </div>

          {/* Heading */}
          <h1
            className={`text-2xl font-bold text-[#0B0B0F] tracking-tight mb-2 transition-all duration-500 delay-200 ${
              checkVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2"
            }`}
            data-testid="success-heading"
          >
            {isClosedLoop
              ? closedStatus === "disqualified" ? "Lead disqualified"
              : closedStatus === "nurture"      ? "Lead moved to nurture"
              : "Lead marked invalid"
              : "Prospect created and assigned"}
          </h1>
          <p
            className={`text-neutral-500 text-sm leading-relaxed max-w-sm transition-all duration-500 delay-300 ${
              checkVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2"
            }`}
          >
            {isClosedLoop ? (
              <>The BDR closed the loop. This lead will not progress to AE assignment. It now appears in the <span className="font-semibold text-[#0B0B0F]">{closedStatus}</span> bucket.</>
            ) : (
              <>Assigned to <span className="font-semibold text-[#0B0B0F]">{aeName}</span>. They will see this on their dashboard immediately.</>
            )}
          </p>
        </div>

        {/* What happened summary */}
        <div
          className={`mb-8 transition-all duration-500 delay-500 ${
            checkVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2"
          }`}
        >
          <div className="bg-white rounded-2xl border border-neutral-200 shadow-sm overflow-hidden">
            <div className="px-5 pt-4 pb-2 border-b border-neutral-100">
              <div className="text-[10px] uppercase tracking-[0.18em] text-neutral-500 font-semibold">
                What just happened
              </div>
            </div>
            <div className="divide-y divide-neutral-100">
              <StepConfirm
                icon={
                  <path d="M3 9l4-4 4 4M7 5v10M13 9h4M13 13h6M13 17h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                }
                label="Lead saved to Supabase"
                sublabel="Prospect row created with your tenant_id"
              />
              <StepConfirm
                icon={
                  <path d="M9 3H5a2 2 0 00-2 2v4m6-6h10a2 2 0 012 2v4M9 3v10m4 0l-4 4-4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                }
                label="Account created as Prospect"
                sublabel={`Status: Prospect · owner_user_id set to ${aeName}`}
              />
              <StepConfirm
                icon={
                  <>
                    <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                    <circle cx="12" cy="7" r="4" stroke="currentColor" strokeWidth="1.5" />
                  </>
                }
                label={`Assigned to ${aeName}`}
                sublabel="Appears on their AE dashboard in real time"
              />
            </div>
          </div>
        </div>

        {/* CTA buttons */}
        <div
          className={`flex flex-col sm:flex-row gap-3 transition-all duration-500 delay-700 ${
            checkVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2"
          }`}
        >
          {!isClosedLoop && (
          <button
            type="button"
            onClick={handleViewProspect}
            className="flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-[#0B0B0F] text-white text-sm font-bold tracking-wide hover:bg-[#374151] transition-all duration-150 shadow-sm"
          >
            View prospect
            {autoNavigate && countdown > 0 && (
              <span className="ml-1 text-[11px] font-normal text-neutral-300">
                ({countdown}s)
              </span>
            )}
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
              <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          )}
          {isClosedLoop && (
            <button
              type="button"
              onClick={() => navigate("/dashboards/bdr")}
              className="flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-[#0B0B0F] text-white text-sm font-bold tracking-wide hover:bg-[#374151] transition-all duration-150 shadow-sm"
            >
              Back to BDR queue
            </button>
          )}
          <button
            type="button"
            onClick={handleAddAnother}
            className="flex items-center justify-center gap-2 px-6 py-3 rounded-xl border border-neutral-200 text-sm font-bold text-neutral-700 hover:bg-neutral-50 transition-colors"
          >
            Add another lead
          </button>
        </div>

        {/* Cancel auto-navigate */}
        {autoNavigate && countdown > 0 && (
          <button
            type="button"
            onClick={cancelAutoNavigate}
            className="mt-3 text-[11px] text-neutral-500 hover:text-neutral-600 transition-colors underline underline-offset-2"
          >
            Cancel auto-advance
          </button>
        )}
      </main>
    </div>
  );
}

// ─── Step confirmation row ────────────────────────────────────────────────────
function StepConfirm({
  icon,
  label,
  sublabel,
}: {
  icon: React.ReactNode;
  label: string;
  sublabel: string;
}) {
  return (
    <div className="flex items-center gap-3 px-5 py-3.5">
      <div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0" style={{ background: "#0B0B0F0A" }}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" className="text-[#0B0B0F]" aria-hidden>
          {icon}
        </svg>
      </div>
      <div className="min-w-0">
        <div className="text-sm font-semibold text-[#0B0B0F]">{label}</div>
        <div className="text-[11px] text-neutral-500 mt-0.5">{sublabel}</div>
      </div>
      <div className="ml-auto shrink-0">
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
          <circle cx="8" cy="8" r="7" fill="#22c55e20" />
          <path d="M5 8l2 2 4-4" stroke="#16a34a" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>
    </div>
  );
}
